/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../../theme/theme"
import { css } from "@mui/material"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Box,
    Typography,
    Divider,
    Chip
} from "@mui/material"
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FAQItem } from "../../context/QuestionnaireFormContext"

const DialogTitleStyle = withMyTheme((theme) => css`
    font-size: 1.5rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
`)

const AddCustomSectionStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
`)

const SectionTitleStyle = withMyTheme((theme) => css`
    font-size: 1.1rem;
    font-weight: 600;
    color: ${theme.palette.text.primary};
    margin-top: 1rem;
    margin-bottom: 0.5rem;
`)

const ButtonGroupStyle = withMyTheme((theme) => css`
    display: flex;
    gap: 0.5rem;
`)

const AddedQuestionButtonStyle = withMyTheme((theme) => css`
    &.MuiListItemButton-root {
        background-color: ${theme.palette.action.selected};
        
        &:hover {
            background-color: ${theme.palette.action.hover};
        }
    }
`)

const AddedBadgeStyle = withMyTheme((theme) => css`
    margin-left: auto;
    background-color: ${theme.palette.success.main};
    color: white;
`)

interface QuestionSelectorModalProps {
    open: boolean
    onClose: () => void
    onSelectQuestion: (question: string, isCustom: boolean) => void
    addedQuestions: FAQItem[]
}

const PREDEFINED_QUESTIONS_KEYS = [
    'transport',
    'parking',
    'wishes',
    'flowers',
    'veganMenu',
    'breakfast',
    'arrival',
    'hotelTime'
]

export const QuestionSelectorModal = ({
    open,
    onClose,
    onSelectQuestion,
    addedQuestions
}: QuestionSelectorModalProps) => {
    const { t } = useTranslation()
    const [customQuestion, setCustomQuestion] = useState('')

    const handleAddCustomQuestion = () => {
        if (customQuestion.trim()) {
            onSelectQuestion(customQuestion.trim(), true)
            setCustomQuestion('')
            onClose()
        }
    }

    const handleSelectPredefinedQuestion = (questionKey: string) => {
        const question = t(`questionnaire.guestsFAQ.predefinedQuestions.${questionKey}`)
        onSelectQuestion(question, false)
        onClose()
    }

    const handleSelectAddedQuestion = (question: string) => {
        onSelectQuestion(question, false)
        onClose()
    }

    // Check which predefined questions are already added
    const addedQuestionTexts = addedQuestions.map(faq => faq.question)
    const isQuestionAdded = (questionKey: string) => {
        const question = t(`questionnaire.guestsFAQ.predefinedQuestions.${questionKey}`)
        return addedQuestionTexts.includes(question)
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle css={DialogTitleStyle}>
                {t('questionnaire.guestsFAQ.modalTitle')}
            </DialogTitle>
            <DialogContent>
                {/* Add Custom Question Section */}
                <Box css={AddCustomSectionStyle}>
                    <TextField
                        label={t('questionnaire.guestsFAQ.customQuestionLabel')}
                        placeholder={t('questionnaire.guestsFAQ.customQuestionPlaceholder')}
                        value={customQuestion}
                        onChange={(e) => setCustomQuestion(e.target.value)}
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <Box css={ButtonGroupStyle}>
                        <Button
                            variant="contained"
                            onClick={handleAddCustomQuestion}
                            disabled={!customQuestion.trim()}
                            fullWidth
                        >
                            {t('questionnaire.guestsFAQ.addButton')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            fullWidth
                        >
                            {t('questionnaire.guestsFAQ.cancelButton')}
                        </Button>
                    </Box>
                </Box>

                <Divider />

                {/* Added Questions Section */}
                {addedQuestions.length > 0 && (
                    <>
                        <Typography css={SectionTitleStyle}>
                            {t('questionnaire.guestsFAQ.addedQuestions')}
                        </Typography>
                        <List>
                            {addedQuestions.map((faq) => (
                                <ListItem key={faq.id} disablePadding>
                                    <ListItemButton onClick={() => handleSelectAddedQuestion(faq.question)}>
                                        <ListItemText primary={faq.question} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <Divider />
                    </>
                )}

                {/* Suggested Questions Section */}
                <>
                    <Typography css={SectionTitleStyle}>
                        {t('questionnaire.guestsFAQ.suggestedQuestions')}
                    </Typography>
                    <List>
                        {PREDEFINED_QUESTIONS_KEYS.map((key) => {
                            const isAdded = isQuestionAdded(key)
                            return (
                                <ListItem key={key} disablePadding>
                                    <ListItemButton 
                                        onClick={() => handleSelectPredefinedQuestion(key)}
                                        css={isAdded ? AddedQuestionButtonStyle : undefined}
                                    >
                                        <ListItemText
                                            primary={t(`questionnaire.guestsFAQ.predefinedQuestions.${key}`)}
                                        />
                                        {isAdded && (
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label={t('questionnaire.guestsFAQ.addedBadge')}
                                                size="small"
                                                css={AddedBadgeStyle}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            )
                        })}
                    </List>
                </>
            </DialogContent>
        </Dialog>
    )
}
